/**
 * Sector operations — Construction Reports pure types, validators, and content builders.
 * Implements R39 B7.a (plan docs/plans/sector-operations.md row B7 :67, bullets :768-770; SO-D14 :315).
 */

export const CON_REPORT_KINDS = [
  'APPROVED_DPR',
  'HINDRANCE_AGEING',
  'OVERDUE_QUALITY',
  'ATTENDANCE_PROVISIONAL',
  'HANDOVER_INDEX',
] as const
export type ConReportKind = (typeof CON_REPORT_KINDS)[number]

export const MAX_CON_REPORT_RANGE_DAYS = 92
export const MAX_CON_REPORT_ROWS = 500

export const SO_D14_ATTENDANCE_PROVISIONAL_NOTICE =
  'Operational attendance (provisional), not payroll or a statutory muster'

export interface GetConReportInput {
  workspaceId: string
  projectId: string
  kind: ConReportKind
  fromDay?: string
  toDay?: string
  opId?: string
}

export interface ConReportProjectSummary {
  id: string
  name: string
  code: string
  status: string
  placeId: string
  startDate?: string | null
  endDate?: string | null
}

export interface ConReportResult<TRow = unknown, TTotals = unknown, TKind extends ConReportKind = ConReportKind> {
  kind: TKind
  project: ConReportProjectSummary
  fromDay?: string
  toDay?: string
  rows: TRow[]
  totals: TTotals
  generatedAt: number
  timezone: string
  truncated: boolean
}
export type ConReportStructuredResult<TRow = unknown, TTotals = unknown, TKind extends ConReportKind = ConReportKind> = ConReportResult<TRow, TTotals, TKind>

// ---------------------------------------------------------------------------
// 1. APPROVED_DPR Types
// ---------------------------------------------------------------------------

export interface ConDprReportRow {
  id: string
  date: string
  shiftId?: string
  totalHeadcount: number
  gangCount: number
  keyLinesCount: number
  keyLines: Array<{
    trade: string
    qty: number
    unit: string
    headcount: number
    placeId?: string
  }>
  safetyExceptions: boolean
  safetyNotes?: string | null
}

export interface ConDprIsoWeekRollup {
  isoWeek: string
  dprCount: number
  totalHeadcount: number
  safetyExceptions: number
}

export interface ConDprReportTotals {
  totalDprs: number
  totalHeadcount: number
  totalSafetyExceptions: number
  weeklyRollup: ConDprIsoWeekRollup[]
}

// ---------------------------------------------------------------------------
// 2. HINDRANCE_AGEING Types
// ---------------------------------------------------------------------------

export type HindranceAgeBucket = '0-3' | '4-7' | '8-14' | '15-30' | '>30'

export interface ConHindranceReportRow {
  id: string
  title: string
  kind: string
  firstNoticedAt: number
  ageDays: number
  ageBucket: HindranceAgeBucket
  responsibleParty: string
  overdue: boolean
  responseDueAt?: number | null
}

export interface ConHindranceReportTotals {
  totalOpen: number
  totalOverdue: number
  buckets: Record<HindranceAgeBucket, number>
}

// ---------------------------------------------------------------------------
// 3. OVERDUE_QUALITY Types
// ---------------------------------------------------------------------------

export type OverdueQualityCategory = 'INSPECTION' | 'FAILED_TEST' | 'NCR' | 'SNAG'

export interface ConOverdueQualityReportRow {
  id: string
  category: OverdueQualityCategory
  title: string
  placeId?: string
  status: string
  dueAtOrPlannedAt: number
  daysOverdue: number
  assigneeUid?: string | null
}

export interface ConOverdueQualityReportTotals {
  overdueInspectionsCount: number
  failedTestsWithoutNcrCount: number
  openOverdueNcrsCount: number
  openOverdueSnagsCount: number
  totalOverdue: number
  byCategory?: Record<OverdueQualityCategory, number>
}

// ---------------------------------------------------------------------------
// 4. ATTENDANCE_PROVISIONAL Types
// ---------------------------------------------------------------------------

export interface ConAttendanceReportRow {
  id: string
  dayKey: string
  personKey: string
  personName: string
  personKind: 'MEMBER' | 'WORKER'
  status: string
  shiftId: string
  pendingCorrection: boolean
  notice?: string
  actualIn?: string | null
  actualOut?: string | null
}

export interface ConAttendanceReportTotals {
  totalPresent: number
  totalAbsent: number
  totalHalfDay: number
  totalLate: number
  totalOnLeave: number
  totalMarked: number
  pendingCorrectionsCount: number
  notice: string
  presentCount?: number
  absentCount?: number
  provisionalNotice?: string
}

// ---------------------------------------------------------------------------
// 5. HANDOVER_INDEX Types
// ---------------------------------------------------------------------------

export interface ConHandoverSourceCount {
  source: string
  label: string
  stateCounts: Record<string, number>
  totalCount: number
}

export interface ConHandoverEvidenceExportSummary {
  id: string
  state: string
  requestedBy: string
  requestedByName?: string
  createdAt: number
  expiresAt?: string | null
}

export interface ConHandoverIndexTotals {
  totalRecords: number
  latestExports: ConHandoverEvidenceExportSummary[]
}

export type ConDprReportResult = ConReportResult<ConDprReportRow, ConDprReportTotals, 'APPROVED_DPR'>
export type ConHindranceReportResult = ConReportResult<ConHindranceReportRow, ConHindranceReportTotals, 'HINDRANCE_AGEING'>
export type ConOverdueQualityReportResult = ConReportResult<ConOverdueQualityReportRow, ConOverdueQualityReportTotals, 'OVERDUE_QUALITY'>
export type ConAttendanceReportResult = ConReportResult<ConAttendanceReportRow, ConAttendanceReportTotals, 'ATTENDANCE_PROVISIONAL'>
export type ConHandoverIndexReportResult = ConReportResult<ConHandoverSourceCount, ConHandoverIndexTotals, 'HANDOVER_INDEX'>

export type AnyConReportResult =
  | ConDprReportResult
  | ConHindranceReportResult
  | ConOverdueQualityReportResult
  | ConAttendanceReportResult
  | ConHandoverIndexReportResult

// ---------------------------------------------------------------------------
// Pure Validators
// ---------------------------------------------------------------------------

export function validateConReportDayKey(dayKey: string, fieldName: string): string {
  const trimmed = typeof dayKey === 'string' ? dayKey.trim() : ''
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    throw new Error(`Invalid ${fieldName}: must be YYYY-MM-DD day key.`)
  }
  const [yearStr, monthStr, dateStr] = trimmed.split('-')
  const y = Number(yearStr)
  const m = Number(monthStr)
  const d = Number(dateStr)
  if (y < 2000 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) {
    throw new Error(`Invalid ${fieldName}: out-of-range calendar date.`)
  }
  return trimmed
}

export function validateConReportInput(raw: unknown): GetConReportInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const obj = raw as Record<string, unknown>
  const workspaceId = typeof obj.workspaceId === 'string' ? obj.workspaceId.trim() : ''
  if (!workspaceId) {
    throw new Error('workspaceId is required.')
  }
  const projectId = typeof obj.projectId === 'string' ? obj.projectId.trim() : ''
  if (!projectId) {
    throw new Error('projectId is required.')
  }
  const kind = obj.kind as ConReportKind
  if (!CON_REPORT_KINDS.includes(kind)) {
    throw new Error(`Invalid report kind "${String(obj.kind)}". Must be one of ${CON_REPORT_KINDS.join(', ')}.`)
  }

  let fromDay: string | undefined
  let toDay: string | undefined

  if (kind === 'APPROVED_DPR' || kind === 'ATTENDANCE_PROVISIONAL') {
    if (!obj.fromDay || typeof obj.fromDay !== 'string') {
      throw new Error('fromDay is required for range-based construction reports.')
    }
    if (!obj.toDay || typeof obj.toDay !== 'string') {
      throw new Error('toDay is required for range-based construction reports.')
    }
  }

  if (kind !== 'HANDOVER_INDEX' && (obj.fromDay || obj.toDay)) {
    if (obj.fromDay && typeof obj.fromDay === 'string') {
      fromDay = validateConReportDayKey(obj.fromDay, 'fromDay')
    }
    if (obj.toDay && typeof obj.toDay === 'string') {
      toDay = validateConReportDayKey(obj.toDay, 'toDay')
    }

    if (fromDay && toDay) {
      if (toDay < fromDay) {
        throw new Error(`toDay (${toDay}) must be greater than or equal to fromDay (${fromDay}).`)
      }

      const startMs = Date.parse(`${fromDay}T00:00:00Z`)
      const endMs = Date.parse(`${toDay}T00:00:00Z`)
      const diffDays = Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1
      if (diffDays > MAX_CON_REPORT_RANGE_DAYS) {
        throw new Error(`Date range exceeds maximum of ${MAX_CON_REPORT_RANGE_DAYS} days (requested ${diffDays} days).`)
      }
    }
  }

  return {
    workspaceId,
    projectId,
    kind,
    fromDay,
    toDay,
    opId: typeof obj.opId === 'string' ? obj.opId.trim() : undefined,
  }
}

export const validateGetConReportInput = validateConReportInput

// ---------------------------------------------------------------------------
// Pure Helpers: Age Buckets & ISO Week
// ---------------------------------------------------------------------------

export function computeHindranceAgeBucket(ageDays: number): HindranceAgeBucket {
  if (ageDays <= 3) return '0-3'
  if (ageDays <= 7) return '4-7'
  if (ageDays <= 14) return '8-14'
  if (ageDays <= 30) return '15-30'
  return '>30'
}

export function computeIsoWeek(dayKey: string): string {
  // dayKey: YYYY-MM-DD
  const date = new Date(`${dayKey}T00:00:00Z`)
  // ISO week calculation: Thursday in the target week determines the year
  const dayOfWeek = (date.getUTCDay() + 6) % 7 // Monday = 0, Sunday = 6
  date.setUTCDate(date.getUTCDate() - dayOfWeek + 3)
  const firstThursday = date.getTime()
  date.setUTCMonth(0, 1)
  if (date.getUTCDay() !== 4) {
    date.setUTCMonth(0, 1 + ((4 - date.getUTCDay() + 7) % 7))
  }
  const weekNum = 1 + Math.ceil((firstThursday - date.getTime()) / (7 * 24 * 60 * 60 * 1000))
  const weekYear = new Date(firstThursday).getUTCFullYear()
  return `${weekYear}-W${String(weekNum).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Report Builders (matching GeneratedReportContent shape)
// ---------------------------------------------------------------------------

export interface GeneratedReportContent {
  title: string
  executiveSummary: string
  keyMetrics?: Array<{ label: string; value: string; detail: string }>
  visualAssets?: Array<{ id: string; title: string; description: string; mimeType: 'image/png'; dataUrl: string }>
  sections: Array<{ title: string; body: string; bullets: string[] }>
  timelineHighlights: string[]
  risks: string[]
  nextActions: string[]
  calendarDates: Array<{ title: string; date: string; description: string }>
  appendix?: Array<{ title: string; rows: Array<Record<string, unknown>> }>
}

export function buildApprovedDprReportContent(
  result: ConReportResult<ConDprReportRow, ConDprReportTotals>,
): GeneratedReportContent {
  const p = result.project
  const totals = result.totals
  return {
    title: `${p.name} (${p.code}) — Approved DPR Report`,
    executiveSummary: `Approved DPR report covering ${result.fromDay ?? ''} to ${result.toDay ?? ''}. Total of ${totals.totalDprs} approved daily progress reports with ${totals.totalHeadcount} cumulative gang headcount and ${totals.totalSafetyExceptions} safety exceptions recorded.`,
    keyMetrics: [
      { label: 'Approved DPRs', value: String(totals.totalDprs), detail: result.truncated ? 'Truncated at 500' : 'Complete' },
      { label: 'Cumulative Headcount', value: String(totals.totalHeadcount), detail: 'Across all approved shifts' },
      { label: 'Safety Exceptions', value: String(totals.totalSafetyExceptions), detail: totals.totalSafetyExceptions > 0 ? 'Requires attention' : 'None reported' },
    ],
    sections: [
      {
        title: 'Weekly Production Roll-up',
        body: 'ISO weekly summary of approved progress and deployment.',
        bullets: totals.weeklyRollup.map(
          (w) => `Week ${w.isoWeek}: ${w.dprCount} DPRs, ${w.totalHeadcount} headcount, ${w.safetyExceptions} safety exceptions`,
        ),
      },
      {
        title: 'Daily Progress Entries',
        body: 'Approved daily reports for the period.',
        bullets: result.rows.slice(0, 15).map(
          (r) => `${r.date}: ${r.totalHeadcount} workers across ${r.gangCount} gangs (${r.keyLinesCount} activity lines)${r.safetyExceptions ? ' [SAFETY EXCEPTION]' : ''}`,
        ),
      },
    ],
    timelineHighlights: totals.weeklyRollup.map((w) => `${w.isoWeek}: ${w.totalHeadcount} headcount`),
    risks: totals.totalSafetyExceptions > 0
      ? [`${totals.totalSafetyExceptions} safety exceptions flagged across approved DPRs.`]
      : [],
    nextActions: [
      'Ensure submitted DPRs are reviewed and approved on schedule.',
      'Investigate safety exception notes with relevant site supervisors.',
    ],
    calendarDates: [],
    appendix: [
      {
        title: 'Weekly Summary',
        rows: totals.weeklyRollup.map((w) => ({
          Week: w.isoWeek,
          Dprs: w.dprCount,
          Headcount: w.totalHeadcount,
          SafetyExceptions: w.safetyExceptions,
        })),
      },
    ],
  }
}

export function buildHindranceAgeingReportContent(
  result: ConReportResult<ConHindranceReportRow, ConHindranceReportTotals>,
): GeneratedReportContent {
  const p = result.project
  const totals = result.totals
  return {
    title: `${p.name} (${p.code}) — Hindrance & RFI Ageing Report`,
    executiveSummary: `Open hindrances and RFIs report for ${p.name}. Total of ${totals.totalOpen} active obstructions (${totals.totalOverdue} overdue for response). Overdue items must be prioritized to avoid downstream progress disruption.`,
    keyMetrics: [
      { label: 'Total Open', value: String(totals.totalOpen), detail: result.truncated ? 'Truncated at 500' : 'Complete' },
      { label: 'Overdue for Response', value: String(totals.totalOverdue), detail: `${totals.totalOverdue > 0 ? 'Action required' : 'On schedule'}` },
      { label: 'Aged > 30 Days', value: String(totals.buckets['>30']), detail: 'Critical blockage risk' },
    ],
    sections: [
      {
        title: 'Ageing Distribution',
        body: 'Age profile from first noticed date.',
        bullets: [
          `0–3 days: ${totals.buckets['0-3']}`,
          `4–7 days: ${totals.buckets['4-7']}`,
          `8–14 days: ${totals.buckets['8-14']}`,
          `15–30 days: ${totals.buckets['15-30']}`,
          `>30 days: ${totals.buckets['>30']}`,
        ],
      },
      {
        title: 'Active Hindrances & RFIs',
        body: 'Open obstruction items pending resolution.',
        bullets: result.rows.slice(0, 15).map(
          (r) => `${r.title} (${r.kind}) — ${r.ageDays}d old (${r.ageBucket}) [${r.responsibleParty}]${r.overdue ? ' [OVERDUE]' : ''}`,
        ),
      },
    ],
    timelineHighlights: result.rows.filter((r) => r.overdue).map((r) => `${r.title}: ${r.ageDays} days`),
    risks: totals.totalOverdue > 0
      ? [`${totals.totalOverdue} hindrances or RFIs are overdue for response.`]
      : [],
    nextActions: [
      'Escalate hindrances aged over 14 days to package contractors and project managers.',
      'Obtain outstanding formal RFI clarifications from consulting teams.',
    ],
    calendarDates: [],
  }
}

export function buildOverdueQualityReportContent(
  result: ConReportResult<ConOverdueQualityReportRow, ConOverdueQualityReportTotals>,
): GeneratedReportContent {
  const p = result.project
  const totals = result.totals
  return {
    title: `${p.name} (${p.code}) — Overdue Quality Actions Report`,
    executiveSummary: `Quality exception status for ${p.name}. ${totals.totalOverdue} quality actions are currently overdue, comprising ${totals.overdueInspectionsCount} overdue inspections, ${totals.failedTestsWithoutNcrCount} failed tests without closed NCRs, ${totals.openOverdueNcrsCount} open overdue NCRs, and ${totals.openOverdueSnagsCount} overdue snags.`,
    keyMetrics: [
      { label: 'Total Overdue Quality Actions', value: String(totals.totalOverdue), detail: result.truncated ? 'Truncated at 500' : 'Complete' },
      { label: 'Overdue Inspections', value: String(totals.overdueInspectionsCount), detail: 'Held pour cards pending release' },
      { label: 'Failed Tests w/o Closed NCR', value: String(totals.failedTestsWithoutNcrCount), detail: 'Action required' },
      { label: 'Overdue NCRs & Snags', value: String(totals.openOverdueNcrsCount + totals.openOverdueSnagsCount), detail: `${totals.openOverdueNcrsCount} NCRs, ${totals.openOverdueSnagsCount} snags` },
    ],
    sections: [
      {
        title: 'Overdue Inspections & Quality Actions',
        body: 'Items past inspection hold time or target due date.',
        bullets: result.rows.slice(0, 20).map(
          (r) => `[${r.category}] ${r.title} — ${r.daysOverdue} days past due (${r.status})`,
        ),
      },
    ],
    timelineHighlights: result.rows.slice(0, 10).map((r) => `${r.title}: ${r.daysOverdue}d overdue`),
    risks: totals.totalOverdue > 0
      ? [`${totals.totalOverdue} quality inspections, tests, or NCR/snag actions require immediate resolution.`]
      : [],
    nextActions: [
      'Conduct required pre-pour inspections to release held cards.',
      'Raise and close required NCRs for all failed laboratory test samples.',
      'Verify resolved snags and close completed rectification work.',
    ],
    calendarDates: [],
  }
}

export function buildAttendanceProvisionalReportContent(
  result: ConReportResult<ConAttendanceReportRow, ConAttendanceReportTotals>,
): GeneratedReportContent {
  const p = result.project
  const totals = result.totals
  return {
    title: `${p.name} (${p.code}) — Operational Attendance (Provisional)`,
    executiveSummary: `${SO_D14_ATTENDANCE_PROVISIONAL_NOTICE}. Period ${result.fromDay ?? ''} to ${result.toDay ?? ''}: ${totals.totalMarked} records (${totals.totalPresent} present, ${totals.totalHalfDay} half-day, ${totals.totalAbsent} absent). ${totals.pendingCorrectionsCount} correction requests pending approval.`,
    keyMetrics: [
      { label: 'Present', value: String(totals.totalPresent), detail: `${totals.totalHalfDay} half-day` },
      { label: 'Absent', value: String(totals.totalAbsent), detail: `${totals.totalOnLeave} on leave` },
      { label: 'Pending Corrections', value: String(totals.pendingCorrectionsCount), detail: 'Awaiting decider review' },
    ],
    sections: [
      {
        title: 'Disclaimer & Operational Scope',
        body: totals.notice,
        bullets: [
          'Supervisor roll is operational and provisional.',
          'Not valid for payroll processing or statutory muster compliance.',
          'Pending corrections are shown as pending and never automatically applied.',
        ],
      },
      {
        title: 'Recent Attendance Entries',
        body: 'Sample of marked attendance records.',
        bullets: result.rows.slice(0, 15).map(
          (r) => `${r.dayKey}: ${r.personName} (${r.personKind}) — ${r.status}${r.pendingCorrection ? ' [CORRECTION PENDING]' : ''}`,
        ),
      },
    ],
    timelineHighlights: [`Total Marked: ${totals.totalMarked} across ${result.fromDay} to ${result.toDay}`],
    risks: totals.pendingCorrectionsCount > 0
      ? [`${totals.pendingCorrectionsCount} attendance corrections pending approval.`]
      : [],
    nextActions: [
      'Approve or reject outstanding attendance correction requests.',
      'Ensure supervisor attendance rolls are marked daily at shift completion.',
    ],
    calendarDates: [],
  }
}

export function buildHandoverIndexReportContent(
  result: ConReportResult<ConHandoverSourceCount, ConHandoverIndexTotals>,
): GeneratedReportContent {
  const p = result.project
  const totals = result.totals
  return {
    title: `${p.name} (${p.code}) — Handover & Evidence Index`,
    executiveSummary: `Handover evidence index for ${p.name}. Total of ${totals.totalRecords} records across all construction evidence sources. ${totals.latestExports.length} recent evidence export packages recorded for this project.`,
    keyMetrics: [
      { label: 'Total Evidence Records', value: String(totals.totalRecords), detail: 'Across all 6 construction sources' },
      { label: 'Evidence Export Packages', value: String(totals.latestExports.length), detail: 'Recent export jobs' },
    ],
    sections: [
      {
        title: 'Evidence Records Breakdown',
        body: 'Count by source collection and lifecycle state.',
        bullets: result.rows.map(
          (r) => `${r.label}: ${r.totalCount} total (${Object.entries(r.stateCounts).map(([k, v]) => `${k}: ${v}`).join(', ')})`,
        ),
      },
      {
        title: 'Recent Evidence Exports',
        body: 'Latest evidence packages generated for project handover.',
        bullets: totals.latestExports.map(
          (e) => `Export ${e.id} [${e.state}] by ${e.requestedByName || e.requestedBy} on ${new Date(e.createdAt).toISOString().slice(0, 10)}`,
        ),
      },
    ],
    timelineHighlights: totals.latestExports.map(
      (e) => `${new Date(e.createdAt).toISOString().slice(0, 10)}: Export ${e.id} (${e.state})`,
    ),
    risks: [],
    nextActions: [
      'Export complete evidence pack before project milestone sign-off.',
      'Archive accepted bill-support packs and closed NCR records.',
    ],
    calendarDates: [],
  }
}

export function buildConReportContent(result: ConReportResult<unknown, unknown>): GeneratedReportContent {
  switch (result.kind) {
    case 'APPROVED_DPR':
      return buildApprovedDprReportContent(result as unknown as ConReportResult<ConDprReportRow, ConDprReportTotals>)
    case 'HINDRANCE_AGEING':
      return buildHindranceAgeingReportContent(result as unknown as ConReportResult<ConHindranceReportRow, ConHindranceReportTotals>)
    case 'OVERDUE_QUALITY':
      return buildOverdueQualityReportContent(result as unknown as ConReportResult<ConOverdueQualityReportRow, ConOverdueQualityReportTotals>)
    case 'ATTENDANCE_PROVISIONAL':
      return buildAttendanceProvisionalReportContent(result as unknown as ConReportResult<ConAttendanceReportRow, ConAttendanceReportTotals>)
    case 'HANDOVER_INDEX':
      return buildHandoverIndexReportContent(result as unknown as ConReportResult<ConHandoverSourceCount, ConHandoverIndexTotals>)
    default:
      throw new Error(`Unsupported report kind "${String((result as { kind?: unknown })?.kind)}"`)
  }
}
